import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectHoldAndWinActive,
  selectFeatureSpinComplete,
  selectHoldAndWinCurrentRound,
  selectMenuActive,
  selectSceneTransitionActive,
  selectAutoPlaySpeed,
  selectPendingAutoPlaySpeed,
  selectHoldAndWinResumePopupActive,
  selectHoldAndWinSymbolsReady,
  selectHoldAndWinReelsReady,
  selectHoldAndWinOverlayReelsReady,
  selectIsHoldAndWinMidFeatureReload,
  selectIsHoldAndWinCompletionReload,
  selectBet,
} from "../State/selectors";
import {
  setFeatureSpinComplete,
  setFeatureSpinActive,
  rgsPlay,
  applyPendingAutoplaySpeed,
  setShowHoldAndWinCollector,
} from "../State/actions";
import store from "../State/Store/store";
import { useReelRefs } from "./ReelRefsContext";
import { FEATURES_CONFIG } from "../featuresConfig";
import { GAME_LAYOUT } from "../settings";
import { COIN_SYMBOL_IDS } from "../SymbolData";
import { SOUND_NAME } from "../sounds";
import { soundManager } from "../SoundManager";
import { useAppActivity } from "./AppActivityContext";
import { createPlayButtonRotationAnimation } from "../utilities";
import HoldAndWinSymbolController from "../Games/HoldAndWin/HoldAndWinSymbolController";

/**
 * Manages HoldAndWin reel control (spin, stop, tension mode)
 * - All blank symbols spin simultaneously
 * - Cascading stops with delays
 * - Tension mode activates when blanks <= threshold
 */
export const HoldAndWinControlContext = createContext(null);

export const HoldAndWinControlContextProvider = ({ children }) => {
  const {
    holdAndWinReelRefs,
    holdAndWinTensionOverlayRefs,
    gameContainerRef,
    playButtonRef,
  } = useReelRefs();
  const dispatch = useDispatch();
  const { isActive } = useAppActivity();

  const manualStopTriggeredRef = useRef(false);
  const spinningCountRef = useRef(0);
  const completedCountRef = useRef(0);
  const autoStopTimeoutRef = useRef(null);
  const manualStopCleanupTimeoutRef = useRef(null); // Track manual stop cleanup timeout
  const isSpinningRef = useRef(false); // Track if symbols are currently spinning (prevents spam)
  const prevSpinCompleteRef = useRef(false); // Track previous spin complete state for transition detection

  const tensionSymbolsRef = useRef(new Set());
  const tensionActivationOrderRef = useRef([]);
  const activeTensionSymbolRef = useRef(null);
  const tensionModeActiveRef = useRef(false);
  const tensionTimeoutsRef = useRef([]);
  const activateTensionModeRef = useRef(null);
  const stopTimeoutsRef = useRef(new Map());
  const pendingTensionSymbolsRef = useRef(new Set());
  const tensionActivationTimeoutRef = useRef(null);
  const onCoinLandedCallbacksRef = useRef([]); // Changed to array for multiple callbacks
  const coinCountRef = useRef(0); // Track total coins landed this round
  const tensionShakeStartedRef = useRef(false); // Track if shake has been started this session
  const manualStopDuringTensionRef = useRef(false); // Track if manual stop happened during tension mode

  // Autoplay refs
  const autoplayProcessingRef = useRef(false);
  const autoplayInitializedRef = useRef(false);
  const firstSpinScheduledRef = useRef(false); // Track if first spin timeout is active
  const initialIdleAnimationsTriggeredRef = useRef(false); // Track if initial idle animations have been triggered

  const featureSpinComplete = useSelector(selectFeatureSpinComplete);
  const holdAndWinActive = useSelector(selectHoldAndWinActive);
  const holdAndWinCurrentRound = useSelector(selectHoldAndWinCurrentRound);
  const menuActive = useSelector(selectMenuActive);
  const sceneTransitionActive = useSelector(selectSceneTransitionActive);
  const autoPlaySpeed = useSelector(selectAutoPlaySpeed);
  const pendingAutoPlaySpeed = useSelector(selectPendingAutoPlaySpeed);
  const holdAndWinResumePopupActive = useSelector(
    selectHoldAndWinResumePopupActive,
  );
  const holdAndWinSymbolsReady = useSelector(selectHoldAndWinSymbolsReady);
  const holdAndWinReelsReady = useSelector(selectHoldAndWinReelsReady);
  const holdAndWinOverlayReelsReady = useSelector(
    selectHoldAndWinOverlayReelsReady,
  );
  const isHoldAndWinMidFeatureReload = useSelector(
    selectIsHoldAndWinMidFeatureReload,
  );
  const isHoldAndWinCompletionReload = useSelector(
    selectIsHoldAndWinCompletionReload,
  );
  const bet = useSelector(selectBet);

  // Determine current effective speed (pending takes precedence if set)
  const currentSpeed = useMemo(
    () => pendingAutoPlaySpeed || autoPlaySpeed,
    [pendingAutoPlaySpeed, autoPlaySpeed],
  );

  // Memoize totalSymbols calculation (static value: 20 total)
  const totalSymbols = useMemo(
    () => GAME_LAYOUT.REEL_COUNT * GAME_LAYOUT.SYMBOLS_VISIBLE,
    [],
  );

  // REMOVED: No longer need to check refs manually - Redux tracks readiness

  // Gets all blank symbols (symbolId === 0) that should spin
  const getBlankSymbols = useCallback(() => {
    const blankSymbols = [];
    const reels = holdAndWinReelRefs.current;

    if (!reels || !Array.isArray(reels)) {
      return blankSymbols;
    }

    reels.forEach((reel, reelIndex) => {
      if (!reel) {
        return;
      }

      for (let symbolIndex = 0; symbolIndex < 4; symbolIndex++) {
        const symbolRef = reel.getSymbolRef?.(symbolIndex);
        if (!symbolRef?.current) {
          continue;
        }

        // Check if controller is ready
        const isReady = symbolRef.current.isControllerReady?.();
        if (!isReady) {
          continue;
        }

        const controller = symbolRef.current.getSymbolController?.();
        if (!controller) {
          continue;
        }

        if (controller.symbolId === 0) {
          blankSymbols.push({
            reelIndex,
            symbolIndex,
            symbolRef,
          });
        }
      }
    });

    return blankSymbols;
  }, [holdAndWinReelRefs]);

  // Counts current coins on reelset (already landed, not spinning)
  const getCurrentCoinCount = useCallback(() => {
    const reels = holdAndWinReelRefs.current;
    if (!reels || !Array.isArray(reels)) return 0;

    let coinCount = 0;
    reels.forEach((reel) => {
      if (!reel) return;
      for (let symbolIndex = 0; symbolIndex < 4; symbolIndex++) {
        const symbolRef = reel.getSymbolRef?.(symbolIndex);
        const controller = symbolRef?.current?.getSymbolController?.();
        if (
          controller &&
          COIN_SYMBOL_IDS.includes(controller.symbolId) &&
          !controller.isPlaying
        ) {
          coinCount++;
        }
      }
    });
    return coinCount;
  }, [holdAndWinReelRefs]);

  // Gets all currently spinning symbols (isPlaying === true)
  const getSpinningSymbols = useCallback(() => {
    const spinningSymbols = [];
    const reels = holdAndWinReelRefs.current;

    if (!reels || !Array.isArray(reels)) {
      return spinningSymbols;
    }

    reels.forEach((reel, reelIndex) => {
      if (!reel) return;

      for (let symbolIndex = 0; symbolIndex < 4; symbolIndex++) {
        const symbolRef = reel.getSymbolRef?.(symbolIndex);
        const controller = symbolRef?.current?.getSymbolController?.();
        if (controller?.isPlaying) {
          spinningSymbols.push({
            reelIndex,
            symbolIndex,
            symbolRef,
            controller,
          });
        }
      }
    });

    return spinningSymbols;
  }, [holdAndWinReelRefs]);

  // Pre-calculates which symbols should enter tension based on API response
  const calculateTensionEligibility = useCallback(
    (newReels) => {
      if (!FEATURES_CONFIG.HOLD_AND_WIN.TENSION.ENABLED) {
        pendingTensionSymbolsRef.current.clear();
        return;
      }

      // Skip tension in turbo mode (too fast for tension to activate properly)
      if (currentSpeed === "turbo") {
        pendingTensionSymbolsRef.current.clear();
        return;
      }

      if (!newReels || !Array.isArray(newReels)) {
        pendingTensionSymbolsRef.current.clear();
        return;
      }

      // Count current coins already on the board (not spinning)
      const currentCoins = getCurrentCoinCount();

      // Count how many symbols are currently spinning
      const spinningSymbols = getSpinningSymbols();
      const spinningCount = spinningSymbols.length;

      const coinThreshold = FEATURES_CONFIG.HOLD_AND_WIN.TENSION.COIN_THRESHOLD;

      // Calculate remaining blanks (spaces that can be filled)
      const remainingBlanks = totalSymbols - currentCoins;

      // NEW LOGIC: Tension activates when we have 2 or fewer blanks remaining
      // This means: 18+ coins on board, and we can fill the remaining spaces
      // Examples:
      // - 18 coins, 2 blanks, 2 spinning → tension on both
      // - 19 coins, 1 blank, 1 spinning → tension on that one
      // - 18 coins, 2 blanks, 3 spinning → tension on all 3 (any can fill the 2 spots)
      const shouldActivateTension =
        currentCoins >= coinThreshold &&
        remainingBlanks <= 2 &&
        spinningCount > 0;

      if (shouldActivateTension) {
        // Mark ALL currently spinning symbols for tension
        pendingTensionSymbolsRef.current.clear();
        spinningSymbols.forEach(({ reelIndex, symbolIndex }) => {
          pendingTensionSymbolsRef.current.add(`${reelIndex}-${symbolIndex}`);
        });

        // Activate tension immediately since we're already at threshold
        // Clear any pending activation timeout to prevent race conditions
        if (tensionActivationTimeoutRef.current) {
          clearTimeout(tensionActivationTimeoutRef.current);
          tensionActivationTimeoutRef.current = null;
        }

        // Small delay to ensure symbols have started spinning
        tensionActivationTimeoutRef.current = setTimeout(() => {
          if (
            pendingTensionSymbolsRef.current.size > 0 &&
            !tensionModeActiveRef.current
          ) {
            activateTensionModeRef.current?.();
          }
          tensionActivationTimeoutRef.current = null;
        }, 50);
      } else {
        // Clear pending tension symbols if threshold not met
        if (pendingTensionSymbolsRef.current.size > 0) {
          pendingTensionSymbolsRef.current.clear();
        }
        // Clear any pending activation timeout
        if (tensionActivationTimeoutRef.current) {
          clearTimeout(tensionActivationTimeoutRef.current);
          tensionActivationTimeoutRef.current = null;
        }
      }
    },
    [getSpinningSymbols, getCurrentCoinCount, currentSpeed, totalSymbols],
  );

  // Triggers cascading stops (sequential with delays, skips tension symbols unless forceStop)
  const triggerCascadingStops = useCallback(
    (forceStop = false, customCascadeDelay = null) => {
      if (tensionModeActiveRef.current && !forceStop) {
        return;
      }

      const spinningSymbols = getSpinningSymbols();
      const isTurboMode = currentSpeed === "turbo";

      // In auto-stop, each blank plays its own sound (no marking needed)
      // shouldPlayStopSound will remain false, allowing individual sounds
      spinningSymbols.forEach((symbolInfo, index) => {
        const uniqueId = `${symbolInfo.reelIndex}-${symbolInfo.symbolIndex}`;
        if (tensionSymbolsRef.current.has(uniqueId) && !forceStop) {
          return;
        }

        const existingTimeout = stopTimeoutsRef.current.get(uniqueId);
        if (existingTimeout) {
          clearTimeout(existingTimeout);
          stopTimeoutsRef.current.delete(uniqueId);
        }

        const baseDelay = FEATURES_CONFIG.HOLD_AND_WIN.SYMBOL_STOP_BASE_DELAY;

        // Determine cascade delay based on mode
        let cascadeDelay;
        if (customCascadeDelay !== null) {
          cascadeDelay = customCascadeDelay;
        } else if (isTurboMode) {
          cascadeDelay =
            FEATURES_CONFIG.HOLD_AND_WIN.TURBO?.SYMBOL_STOP_CASCADE_DELAY ?? 0;
        } else {
          cascadeDelay = FEATURES_CONFIG.HOLD_AND_WIN.SYMBOL_STOP_CASCADE_DELAY;
        }

        const delay = baseDelay + index * cascadeDelay;

        const timeoutId = setTimeout(() => {
          // Tension is now ONLY activated at spin start via calculateTensionEligibility
          // No dynamic checks during cascading stops - keeps logic simple
          try {
            // Call appropriate stop method based on mode
            if (isTurboMode) {
              symbolInfo.symbolRef.current?.stopSpinTurbo();
            } else {
              symbolInfo.symbolRef.current?.stopSpin();
            }
          } catch (error) {
            // Silent fail
          }
          stopTimeoutsRef.current.delete(uniqueId);
        }, delay);

        stopTimeoutsRef.current.set(uniqueId, timeoutId);
      });
    },
    [getSpinningSymbols, currentSpeed],
  );

  // Handles tension symbol completion (parallel mode - all symbols stop independently)
  const handleTensionSymbolComplete = useCallback(
    (reelIndex, symbolIndex) => {
      const uniqueId = `${reelIndex}-${symbolIndex}`;

      if (!tensionSymbolsRef.current.has(uniqueId)) {
        return;
      }

      // Remove this symbol from active tension symbols
      tensionSymbolsRef.current.delete(uniqueId);

      // Stop tension animation on dedicated tension overlay
      const tensionOverlay =
        holdAndWinTensionOverlayRefs.current?.[reelIndex]?.[symbolIndex];
      if (tensionOverlay?.playTensionOut) {
        tensionOverlay.playTensionOut();
      }

      // Check if all tension symbols have completed
      if (tensionSymbolsRef.current.size === 0) {
        // All tension symbols completed - exit tension mode
        tensionModeActiveRef.current = false;
        tensionActivationOrderRef.current = [];
        activeTensionSymbolRef.current = null;

        // DON'T stop shake here - keep shaking until collection starts
        // Only stop tension sound for this round
        soundManager.stop(SOUND_NAME.feature_tension);

        // Trigger cascading stops for any remaining non-tension symbols
        triggerCascadingStops();
      }
    },
    [triggerCascadingStops, holdAndWinTensionOverlayRefs],
  );

  // Called when symbol completes stop animation (checks tension activation, dispatches spinComplete)
  const handleSymbolStopComplete = useCallback(
    (symbolIndex, reelIndex) => {
      completedCountRef.current++;

      const uniqueId = `${reelIndex}-${symbolIndex}`;
      const isTensionSymbol =
        tensionSymbolsRef.current.has(uniqueId) && tensionModeActiveRef.current;

      if (isTensionSymbol) {
        // Tension animation is already stopped in showLandingAnimation()
        // No need to stop it again here to avoid double cleanup

        handleTensionSymbolComplete(reelIndex, symbolIndex);
        // Check completion after handling tension symbol
        if (completedCountRef.current >= spinningCountRef.current) {
          soundManager.stop(SOUND_NAME.feature_reel_spin);
          isSpinningRef.current = false; // CRITICAL: Reset spinning flag so next spin can start
          dispatch(setFeatureSpinComplete({ complete: true, bet }));
        }
        return;
      }

      // Only check for tension activation if not a tension symbol
      const symbolRef =
        holdAndWinReelRefs.current?.[reelIndex]?.getSymbolRef?.(symbolIndex);
      const controller = symbolRef?.current?.getSymbolController?.();
      let landedOnCoin =
        controller &&
        controller.symbolId !== 0 &&
        controller.symbolId !== undefined;

      // Special case: If this is potentially the 20th coin and symbolId is cleared
      // Check if we're at 19 coins and this is the last symbol to stop
      if (
        !landedOnCoin &&
        coinCountRef.current === 19 &&
        completedCountRef.current === spinningCountRef.current
      ) {
        landedOnCoin = true;
      }

      // Notify all registered callbacks that a coin landed (for incremental updates)
      if (landedOnCoin && onCoinLandedCallbacksRef.current.length > 0) {
        coinCountRef.current++;
        onCoinLandedCallbacksRef.current.forEach((callback) => {
          if (callback) callback();
        });
      }

      // Tension is ONLY activated at spin start via calculateTensionEligibility
      // No dynamic checks during symbol stops - keeps logic simple and predictable

      // Single completion check (consolidated to prevent double dispatch)
      if (completedCountRef.current >= spinningCountRef.current) {
        soundManager.stop(SOUND_NAME.feature_reel_spin);
        isSpinningRef.current = false; // All symbols stopped, ready for next spin
        dispatch(setFeatureSpinComplete({ complete: true, bet }));
      }
    },
    [dispatch, holdAndWinReelRefs, handleTensionSymbolComplete, bet],
  );

  // Activates tension mode on pre-calculated spinning symbols (PARALLEL activation - all at once)
  const activateTensionMode = useCallback(() => {
    if (tensionModeActiveRef.current) {
      return;
    }

    // Only activate if we have pre-calculated tension symbols
    if (pendingTensionSymbolsRef.current.size === 0) {
      return;
    }

    const allSpinningSymbols = getSpinningSymbols();

    if (allSpinningSymbols.length === 0) {
      return;
    }

    // Filter to only symbols that were pre-calculated to need tension
    const eligibleTensionSymbols = allSpinningSymbols.filter((symbol) =>
      pendingTensionSymbolsRef.current.has(
        `${symbol.reelIndex}-${symbol.symbolIndex}`,
      ),
    );

    if (eligibleTensionSymbols.length === 0) {
      return;
    }

    // Clear stop timeouts for eligible symbols
    eligibleTensionSymbols.forEach((symbol) => {
      const uniqueId = `${symbol.reelIndex}-${symbol.symbolIndex}`;
      const existingTimeout = stopTimeoutsRef.current.get(uniqueId);
      if (existingTimeout) {
        clearTimeout(existingTimeout);
        stopTimeoutsRef.current.delete(uniqueId);
      }
    });

    tensionModeActiveRef.current = true;
    tensionActivationOrderRef.current = eligibleTensionSymbols.map(
      (symbol) => ({
        reelIndex: symbol.reelIndex,
        symbolIndex: symbol.symbolIndex,
        symbolRef: symbol.symbolRef,
        uniqueId: `${symbol.reelIndex}-${symbol.symbolIndex}`,
      }),
    );

    tensionTimeoutsRef.current.forEach(clearTimeout);
    tensionTimeoutsRef.current = [];

    // Start tension shake animation on game container (only once per Hold and Win session)
    if (!tensionShakeStartedRef.current) {
      gameContainerRef?.current?.playTensionShake?.();
      tensionShakeStartedRef.current = true;
    }

    soundManager.stop(SOUND_NAME.feature_reel_spin);
    soundManager.play(SOUND_NAME.feature_tension);

    // Check if we're in turbo mode
    const isTurboMode = currentSpeed === "turbo";

    // Use turbo-specific timing if in turbo mode
    const tensionDuration = isTurboMode
      ? FEATURES_CONFIG.HOLD_AND_WIN.TENSION.TURBO_DURATION || 800
      : FEATURES_CONFIG.HOLD_AND_WIN.TENSION.DURATION || 3500;

    // Cascading stop delay for tension (specific delay for tension symbols)
    const cascadeDelay = isTurboMode
      ? FEATURES_CONFIG.HOLD_AND_WIN.TENSION.TURBO_CASCADE_DELAY || 150
      : FEATURES_CONFIG.HOLD_AND_WIN.TENSION.CASCADE_DELAY || 750;

    // In turbo mode, find the last blank for sound marking
    let lastBlankIndex = -1;
    if (isTurboMode) {
      for (let i = eligibleTensionSymbols.length - 1; i >= 0; i--) {
        const symbol = eligibleTensionSymbols[i];
        const controller = symbol.symbolRef?.current?.getSymbolController?.();
        if (controller) {
          const pendingSymbolId =
            controller.pendingLandingSymbolId || controller.symbolId;
          if (!COIN_SYMBOL_IDS.includes(pendingSymbolId)) {
            lastBlankIndex = i;
            break;
          }
        }
      }
    }

    // PARALLEL ACTIVATION: Start tension on ALL symbols simultaneously
    // BUT schedule stops with cascading delays (first stops, then second, etc.)
    eligibleTensionSymbols.forEach((symbol, index) => {
      const uniqueId = `${symbol.reelIndex}-${symbol.symbolIndex}`;
      tensionSymbolsRef.current.add(uniqueId);

      try {
        if (symbol.symbolRef?.current) {
          // Start tension spin (keeps normal speed, just extends duration)
          symbol.symbolRef.current.startTensionSpin();

          // Play tension animation on dedicated tension overlay
          const tensionOverlay =
            holdAndWinTensionOverlayRefs.current?.[symbol.reelIndex]?.[
              symbol.symbolIndex
            ];
          if (tensionOverlay?.playTensionIn) {
            tensionOverlay.playTensionIn();
          }

          // Schedule stop with cascading delay: first symbol stops after tensionDuration,
          // second symbol stops after tensionDuration + cascadeDelay, etc.
          const stopDelay = tensionDuration + index * cascadeDelay;

          const timeoutId = setTimeout(() => {
            try {
              if (symbol.symbolRef?.current) {
                // Mark last blank in turbo mode for sound
                const controller =
                  symbol.symbolRef.current.getSymbolController?.();
                if (isTurboMode && controller && index === lastBlankIndex) {
                  controller.shouldPlayStopSound = true;
                }

                // Use appropriate stop method based on current speed mode
                if (isTurboMode) {
                  symbol.symbolRef.current.stopSpinTurbo();
                } else {
                  symbol.symbolRef.current.stopSpin();
                }
              }
            } catch (error) {
              // Silent fail
            }
            tensionTimeoutsRef.current = tensionTimeoutsRef.current.filter(
              (id) => id !== timeoutId,
            );
          }, stopDelay);
          tensionTimeoutsRef.current.push(timeoutId);
        }
      } catch (error) {
        // Silent fail
      }
    });
  }, [
    getSpinningSymbols,
    gameContainerRef,
    holdAndWinTensionOverlayRefs,
    currentSpeed,
  ]);

  useEffect(() => {
    activateTensionModeRef.current = activateTensionMode;
  }, [activateTensionMode]);

  // Exits tension mode (clears timeouts, resets state, cleans up overlay animations)
  // eslint-disable-next-line no-unused-vars
  const exitTensionMode = useCallback(() => {
    tensionTimeoutsRef.current.forEach(clearTimeout);
    tensionTimeoutsRef.current = [];
    stopTimeoutsRef.current.forEach((timeoutId) => clearTimeout(timeoutId));
    stopTimeoutsRef.current.clear();

    tensionSymbolsRef.current.forEach((uniqueId) => {
      const [reelIndex, symbolIndex] = uniqueId.split("-").map(Number);
      const reels = holdAndWinReelRefs.current;
      if (reels && reels[reelIndex]) {
        const symbolRef = reels[reelIndex].getSymbolRef?.(symbolIndex);
        if (symbolRef?.current?.exitTensionMode) {
          try {
            symbolRef.current.exitTensionMode();
          } catch (error) {
            // Silent fail
          }
        }
      }

      // Stop tension animation on dedicated tension overlay
      const tensionOverlay =
        holdAndWinTensionOverlayRefs.current?.[reelIndex]?.[symbolIndex];
      if (tensionOverlay?.playTensionOut) {
        try {
          tensionOverlay.playTensionOut();
        } catch (error) {
          // Silent fail
        }
      }
    });

    tensionModeActiveRef.current = false;
    tensionSymbolsRef.current.clear();
    tensionActivationOrderRef.current = [];
    activeTensionSymbolRef.current = null;

    // DON'T stop shake here - it should continue until collection starts
    // Only stop shake when Hold and Win feature completely ends
  }, [holdAndWinReelRefs, holdAndWinTensionOverlayRefs]);

  // Ref to store handleSpin for use in effects without causing re-renders
  const handleSpinRef = useRef();

  // Starts spin sequence (all blank symbols spin simultaneously, auto-stop after duration)
  const handleSpin = useCallback(() => {
    if (!holdAndWinActive) {
      return;
    }

    // Prevent starting new spin while symbols are already spinning (prevents spam/race conditions)
    if (isSpinningRef.current) {
      return;
    }

    if (autoStopTimeoutRef.current) {
      clearTimeout(autoStopTimeoutRef.current);
      autoStopTimeoutRef.current = null;
    }

    // Mark that we're starting a spin
    isSpinningRef.current = true;
    manualStopTriggeredRef.current = false;

    // CRITICAL: Reset counters at START of spin, not at end
    // This prevents race condition where spinComplete effect resets them
    completedCountRef.current = 0;

    dispatch(setFeatureSpinComplete({ complete: false, bet }));

    // DON'T call exitTensionMode here - we want tension shake to persist across spins
    // Just clear the pending tension symbols for the new spin calculation
    pendingTensionSymbolsRef.current.clear();

    const blankSymbols = getBlankSymbols();
    spinningCountRef.current = blankSymbols.length;

    // Call appropriate spin method based on current speed
    const isTurboMode = currentSpeed === "turbo";
    blankSymbols.forEach(({ symbolRef }) => {
      if (isTurboMode) {
        symbolRef.current?.startSpinTurbo();
      } else {
        symbolRef.current?.startSpin();
      }
    });

    // Trigger rotation animation on play button (same as regular game autoplay)
    if (playButtonRef.current) {
      createPlayButtonRotationAnimation(playButtonRef.current);
    }

    // Play spin sound in both normal and turbo mode
    soundManager.play(SOUND_NAME.feature_reel_spin);

    // Use turbo-specific spin duration if in turbo mode (much shorter for instant drops)
    const spinDuration = isTurboMode
      ? FEATURES_CONFIG.HOLD_AND_WIN.TURBO?.SPIN_DURATION || 100
      : FEATURES_CONFIG.HOLD_AND_WIN.SPIN_DURATION || 500;

    autoStopTimeoutRef.current = setTimeout(() => {
      if (!manualStopTriggeredRef.current) {
        triggerCascadingStops();
      }
    }, spinDuration);

    // Set feature spin active before dispatching rgsPlay
    dispatch(setFeatureSpinActive(true));
    dispatch(rgsPlay());
  }, [
    holdAndWinActive,
    dispatch,
    getBlankSymbols,
    triggerCascadingStops,
    playButtonRef,
    currentSpeed,
    bet,
  ]);

  // Keep ref updated with latest handleSpin
  useEffect(() => {
    handleSpinRef.current = handleSpin;
  }, [handleSpin]);

  // Registers/unregisters callback to be notified when coins land (supports multiple callbacks)
  const registerProgressBarCallback = useCallback((callback) => {
    // Return unregister function
    if (callback) {
      // Add callback if not already registered
      if (!onCoinLandedCallbacksRef.current.includes(callback)) {
        onCoinLandedCallbacksRef.current.push(callback);
      }

      // Return cleanup function to remove this specific callback
      return () => {
        onCoinLandedCallbacksRef.current =
          onCoinLandedCallbacksRef.current.filter((cb) => cb !== callback);
      };
    }
  }, []);

  // Handles manual stop (stops tension symbols immediately, then cascades all)
  const handleManualStop = useCallback(() => {
    if (manualStopTriggeredRef.current) {
      return;
    }

    if (autoStopTimeoutRef.current) {
      clearTimeout(autoStopTimeoutRef.current);
      autoStopTimeoutRef.current = null;
    }

    manualStopTriggeredRef.current = true;

    // If no symbols are spinning, just return (nothing to stop)
    if (!isSpinningRef.current) {
      return;
    }

    // Set manual stop flag - only last blank will play sound
    HoldAndWinSymbolController.isManualStopActive = true;

    const tensionSymbolsSnapshot = Array.from(tensionSymbolsRef.current);

    // CRITICAL: Track if we're manually stopping during tension mode
    // This flag will be used to add extra delay before next autoplay spin
    if (tensionSymbolsSnapshot.length > 0) {
      manualStopDuringTensionRef.current = true;
    }

    // Find the last blank to mark for sound (manual stop = only last blank plays)
    let lastBlankController = null;
    let lastSymbolController = null; // Track the very last symbol (for stopping spin sound)

    // Iterate backwards to find the last blank and last symbol
    for (let i = tensionSymbolsSnapshot.length - 1; i >= 0; i--) {
      const uniqueId = tensionSymbolsSnapshot[i];
      const [reelIndex, symbolIndex] = uniqueId.split("-").map(Number);
      const reels = holdAndWinReelRefs.current;
      if (reels && reels[reelIndex]) {
        const symbolRef = reels[reelIndex].getSymbolRef?.(symbolIndex);
        const controller = symbolRef?.current?.getSymbolController?.();
        if (controller?.isPlaying && !controller?.isStopping) {
          // Mark the very last symbol (first one found when iterating backwards)
          if (!lastSymbolController) {
            lastSymbolController = controller;
          }

          // Find last blank for stop sound
          const pendingSymbolId =
            controller.pendingLandingSymbolId || controller.symbolId;
          if (
            !COIN_SYMBOL_IDS.includes(pendingSymbolId) &&
            !lastBlankController
          ) {
            lastBlankController = controller;
          }
        }
      }
    }

    tensionSymbolsSnapshot.forEach((uniqueId) => {
      const [reelIndex, symbolIndex] = uniqueId.split("-").map(Number);
      const reels = holdAndWinReelRefs.current;
      if (reels && reels[reelIndex]) {
        const symbolRef = reels[reelIndex].getSymbolRef?.(symbolIndex);
        const controller = symbolRef?.current?.getSymbolController?.();
        if (controller?.isPlaying && !controller?.isStopping) {
          // Mark last blank for stop sound
          if (controller === lastBlankController) {
            controller.shouldPlayStopSound = true;
          }

          // Mark last symbol to stop spin sound
          if (controller === lastSymbolController) {
            controller.isLastSymbolToStop = true;
          }

          try {
            symbolRef?.current?.stopSpin();
          } catch (error) {
            // Silent fail
          }
        }
      }
    });

    // Clean up tension mode state for this spin, but DON'T stop the shake
    // (shake should persist until collection starts)
    tensionModeActiveRef.current = false;
    tensionSymbolsRef.current.clear();
    tensionActivationOrderRef.current = [];
    activeTensionSymbolRef.current = null;

    // Reset manual stop flag after all symbols have stopped (give them time to complete)
    manualStopCleanupTimeoutRef.current = setTimeout(() => {
      HoldAndWinSymbolController.isManualStopActive = false;
      manualStopCleanupTimeoutRef.current = null;
    }, 1000);

    // Clean up tension overlays on manual stop
    holdAndWinTensionOverlayRefs.current.forEach((reelOverlays, reelIndex) => {
      if (!reelOverlays) return;
      reelOverlays.forEach((tensionOverlay, symbolIndex) => {
        if (tensionOverlay?.hideTension) {
          try {
            tensionOverlay.hideTension();
          } catch (error) {
            // Ignore errors during cleanup
          }
        }
      });
    });

    // Stop tension sound if active (but keep feature_reel_spin playing until last symbol stops)
    soundManager.stop(SOUND_NAME.feature_tension);

    // Clear any existing manual stop cleanup timeout to prevent buildup
    if (manualStopCleanupTimeoutRef.current) {
      clearTimeout(manualStopCleanupTimeoutRef.current);
      manualStopCleanupTimeoutRef.current = null;
    }

    // Use manual cascade delay based on current speed mode
    // Normal/Auto speed gets faster cascade for better responsiveness
    const isTurboMode = currentSpeed === "turbo";
    const manualCascadeDelay = isTurboMode
      ? FEATURES_CONFIG.HOLD_AND_WIN.SYMBOL_STOP_CASCADE_DELAY_MANUAL || 50
      : FEATURES_CONFIG.HOLD_AND_WIN.SYMBOL_STOP_CASCADE_DELAY_MANUAL_NORMAL ||
        25;

    triggerCascadingStops(true, manualCascadeDelay);
  }, [
    triggerCascadingStops,
    holdAndWinReelRefs,
    holdAndWinTensionOverlayRefs,
    currentSpeed,
  ]);

  // Pre-calculate tension eligibility when API response arrives
  useEffect(() => {
    if (
      holdAndWinActive &&
      holdAndWinCurrentRound?.reels &&
      Array.isArray(holdAndWinCurrentRound.reels)
    ) {
      calculateTensionEligibility(holdAndWinCurrentRound.reels);
    }
  }, [holdAndWinActive, holdAndWinCurrentRound, calculateTensionEligibility]);

  // Cleanup on spin complete (ONLY on false → true transition)
  useEffect(() => {
    // CRITICAL: Only run cleanup when transitioning from false → true
    // This prevents the effect from running when spin starts (true → false → effect runs with stale true value)
    const isTransitionToComplete =
      featureSpinComplete && !prevSpinCompleteRef.current;

    if (isTransitionToComplete) {
      if (autoStopTimeoutRef.current) {
        clearTimeout(autoStopTimeoutRef.current);
        autoStopTimeoutRef.current = null;
      }
      // DON'T call exitTensionMode here - it would reset tension state between spins
      // We want tension shake to persist across all spins until collection starts
      // Just clean up the tension mode state for this round
      tensionModeActiveRef.current = false;
      tensionSymbolsRef.current.clear();
      tensionActivationOrderRef.current = [];
      activeTensionSymbolRef.current = null;

      manualStopTriggeredRef.current = false;

      // CRITICAL: DON'T reset spinningCountRef here!
      // It causes race condition where next spin sets it, then this effect resets it to 0
      // Counters are now reset at START of spin in handleSpin()
      // spinningCountRef.current = 0;  // REMOVED
      // completedCountRef.current = 0; // REMOVED (now in handleSpin)

      // Apply pending autoplay speed (queue system - like main game)
      if (pendingAutoPlaySpeed) {
        dispatch(applyPendingAutoplaySpeed());
      }
    }

    // Update previous value for next comparison
    prevSpinCompleteRef.current = featureSpinComplete;
  }, [featureSpinComplete, pendingAutoPlaySpeed, dispatch]);

  // ============================================================================
  // AUTOPLAY EFFECTS
  // ============================================================================

  /**
   * Autoplay Initialization Effect
   *
   * Triggers the first Hold and Win spin automatically when:
   * - Hold and Win feature is active
   * - Scene transition has completed (sceneTransitionActive = false)
   * - Reload dialog is closed (holdAndWinResumePopupActive = false)
   * - Not in mid-feature reload state (isHoldAndWinMidFeatureReload = false)
   * - Not in completion reload state (isHoldAndWinCompletionReload = false)
   * - Not already initialized
   *
   * Hold and Win is a bonus feature that auto-plays regardless of regular game autoplay state.
   * Waits for a configurable delay after transition completes to allow user to see the feature.
   */
  useEffect(() => {
    if (
      holdAndWinActive &&
      !sceneTransitionActive &&
      !holdAndWinResumePopupActive &&
      !isHoldAndWinMidFeatureReload && // CRITICAL: Don't start autoplay during mid-feature reload
      !isHoldAndWinCompletionReload && // CRITICAL: Don't start autoplay during completion reload
      holdAndWinSymbolsReady && // Wait for all symbol controllers to be ready
      holdAndWinReelsReady && // Wait for all base reel imperative handles to be ready
      holdAndWinOverlayReelsReady && // Wait for all overlay reel imperative handles to be ready
      !autoplayInitializedRef.current
    ) {
      // Mark that we've initialized (idle animations already triggered in scene transition for natural entry)
      initialIdleAnimationsTriggeredRef.current = true;

      // CRITICAL: Set spinComplete to false immediately to prevent continuous effect from firing
      dispatch(setFeatureSpinComplete({ complete: false, bet }));

      // CRITICAL: Set flag IMMEDIATELY (synchronously) to block continuous effect
      firstSpinScheduledRef.current = true;
      autoplayInitializedRef.current = true;

      // Read speed directly from store to avoid effect re-runs on speed changes
      const currentAutoPlaySpeed = store.getState().settings.autoPlaySpeed;
      const isTurboMode = currentAutoPlaySpeed === "turbo";
      const firstSpinDelay = isTurboMode
        ? FEATURES_CONFIG.HOLD_AND_WIN.AUTOPLAY_FIRST_SPIN_DELAY_TURBO || 1000
        : FEATURES_CONFIG.HOLD_AND_WIN.AUTOPLAY_FIRST_SPIN_DELAY || 1000;

      // Delay after transition completes (let user see the feature board)
      const initTimeout = setTimeout(() => {
        firstSpinScheduledRef.current = false;
        if (holdAndWinActive && handleSpinRef.current) {
          handleSpinRef.current();
        }
      }, firstSpinDelay);

      return () => {
        firstSpinScheduledRef.current = false;
        clearTimeout(initTimeout);
      };
    } else if (!holdAndWinActive && autoplayInitializedRef.current) {
      autoplayInitializedRef.current = false;
      firstSpinScheduledRef.current = false;
      initialIdleAnimationsTriggeredRef.current = false;
    }
  }, [
    holdAndWinActive,
    sceneTransitionActive,
    holdAndWinResumePopupActive,
    isHoldAndWinMidFeatureReload,
    isHoldAndWinCompletionReload,
    holdAndWinSymbolsReady,
    holdAndWinReelsReady,
    holdAndWinOverlayReelsReady,
    dispatch,
    bet,
  ]); // currentSpeed removed - read directly from store to prevent timeout resets

  /**
   * Autoplay Continuous Spinning Effect
   *
   * Manages continuous autoplay spins during Hold and Win feature.
   * Automatically triggers next spin after each spin completes.
   * Pauses when menu is active, tab is inactive, or scene transition is active.
   * STOPS when respins reach 0 (feature ending) to allow collector and outro to play.
   * Hold and Win is a bonus feature that auto-plays regardless of regular game autoplay state.
   *
   * CRITICAL: autoPlaySpeed is NOT in dependencies - read directly from store when needed.
   * This prevents timeout resets when user spams turbo button (matches ReelControlContext pattern).
   */
  useEffect(() => {
    // Only run during Hold and Win feature
    if (!holdAndWinActive) {
      if (autoplayProcessingRef.current) {
        autoplayProcessingRef.current = false;
      }
      return;
    }

    // CRITICAL: Don't run until first spin has been initialized AND completed
    // This prevents double-spin on feature entry
    if (!autoplayInitializedRef.current || firstSpinScheduledRef.current) {
      return;
    }

    if (!featureSpinComplete) {
      // Spin is not complete - don't process
      return;
    }

    // Reset processing flag when spin completes (mirrors ReelControlContext pattern)
    // Without this, isActive blur/focus flicker from rapid clicking leaves the flag stuck true,
    // causing every subsequent effect re-run to never schedule the next spin
    autoplayProcessingRef.current = false;

    // CRITICAL: Stop autoplay when respins reach 0 (feature ending)
    // Check current state directly to avoid re-running effect on every round data change
    const state = store.getState();
    const currentRound = state.featureGame.currentRound;
    const respinsLeft = state.featureGame.respinsLeft;
    const hasRoundData =
      currentRound?.reels &&
      Array.isArray(currentRound.reels) &&
      currentRound.reels.length > 0;

    if (hasRoundData && respinsLeft === 0) {
      return;
    }

    // Pause when app/tab is inactive
    if (!isActive) {
      return;
    }

    // Pause when menu is active
    if (menuActive) {
      return;
    }

    // Pause during scene transitions
    if (sceneTransitionActive) {
      return;
    }

    autoplayProcessingRef.current = true;

    // Read speed directly from store to avoid effect re-runs on speed changes
    const currentAutoPlaySpeed = store.getState().settings.autoPlaySpeed;
    const isTurboMode = currentAutoPlaySpeed === "turbo";
    let spinDelay = isTurboMode
      ? FEATURES_CONFIG.HOLD_AND_WIN.AUTOPLAY_SPIN_DELAY_TURBO || 625
      : FEATURES_CONFIG.HOLD_AND_WIN.AUTOPLAY_SPIN_DELAY || 1250;

    // CRITICAL: Add extra delay if we just manually stopped during tension mode
    // Manual stop completes much faster than natural tension completion, so state needs extra time to settle
    if (manualStopDuringTensionRef.current) {
      spinDelay += 300; // Add 300ms buffer for state to fully settle after manual tension stop
    }

    // Schedule next spin with delay
    const timeoutId = setTimeout(() => {
      // Reset manual stop flag before next spin
      manualStopDuringTensionRef.current = false;

      // Double-check feature is still active and hasn't ended
      const state = store.getState();
      const currentRound = state.featureGame.currentRound;
      const currentRespinsLeft = state.featureGame.respinsLeft;
      const hasData = currentRound?.reels != null;

      if (
        holdAndWinActive &&
        !(hasData && currentRespinsLeft === 0) &&
        handleSpinRef.current
      ) {
        handleSpinRef.current();
        // Processing flag is reset inside handleSpin when spin starts
      } else {
        autoplayProcessingRef.current = false;
      }
    }, spinDelay);

    return () => {
      clearTimeout(timeoutId);
      // CRITICAL: Reset processing flag when effect cleanup runs
      // This ensures the flag is cleared if the effect re-runs before timeout fires
      autoplayProcessingRef.current = false;
    };
  }, [
    holdAndWinActive,
    featureSpinComplete,
    menuActive,
    sceneTransitionActive,
    isActive,
    // currentSpeed removed - read directly from store to prevent timeout resets on speed changes
  ]);

  /**
   * Cleanup autoplay state when Hold and Win feature ends
   * Initialize coin count when feature starts
   */
  useEffect(() => {
    if (holdAndWinActive) {
      // Reset all state when feature starts (for consecutive Hold and Win sessions)
      coinCountRef.current = 0;
      manualStopTriggeredRef.current = false;
      manualStopDuringTensionRef.current = false;
      isSpinningRef.current = false;
      spinningCountRef.current = 0;
      completedCountRef.current = 0;
      // Reset collector display flag for new session
      dispatch(setShowHoldAndWinCollector(false));
    } else {
      autoplayProcessingRef.current = false;
      autoplayInitializedRef.current = false;
      isSpinningRef.current = false;
      manualStopTriggeredRef.current = false;
      manualStopDuringTensionRef.current = false;
      tensionShakeStartedRef.current = false;

      // Reset counters when feature ends
      spinningCountRef.current = 0;
      completedCountRef.current = 0;
      coinCountRef.current = 0;

      // CRITICAL: Clear all timeouts when feature ends
      if (autoStopTimeoutRef.current) {
        clearTimeout(autoStopTimeoutRef.current);
        autoStopTimeoutRef.current = null;
      }

      if (manualStopCleanupTimeoutRef.current) {
        clearTimeout(manualStopCleanupTimeoutRef.current);
        manualStopCleanupTimeoutRef.current = null;
      }

      if (tensionActivationTimeoutRef.current) {
        clearTimeout(tensionActivationTimeoutRef.current);
        tensionActivationTimeoutRef.current = null;
      }

      // Clear all stop timeouts
      stopTimeoutsRef.current.forEach((timeoutId) => clearTimeout(timeoutId));
      stopTimeoutsRef.current.clear();

      // Clear all tension timeouts
      tensionTimeoutsRef.current.forEach(clearTimeout);
      tensionTimeoutsRef.current = [];

      // Clear tension state refs
      tensionSymbolsRef.current.clear();
      pendingTensionSymbolsRef.current.clear();
      tensionModeActiveRef.current = false;
      tensionActivationOrderRef.current = [];
      activeTensionSymbolRef.current = null;

      // Reset collector display flag when feature ends
      dispatch(setShowHoldAndWinCollector(false));
    }
  }, [holdAndWinActive, dispatch]);

  // CRITICAL: Clear all tension and stop timeouts when reload flags are active
  // This prevents orphaned timeouts from firing on recreated symbols
  useEffect(() => {
    if (isHoldAndWinMidFeatureReload || isHoldAndWinCompletionReload) {
      // Clear tension activation timeout (prevents race condition)
      if (tensionActivationTimeoutRef.current) {
        clearTimeout(tensionActivationTimeoutRef.current);
        tensionActivationTimeoutRef.current = null;
      }

      // Clear auto-stop timeout
      if (autoStopTimeoutRef.current) {
        clearTimeout(autoStopTimeoutRef.current);
        autoStopTimeoutRef.current = null;
      }

      // Clear all stop timeouts (prevents orphaned stop commands)
      stopTimeoutsRef.current.forEach((timeoutId) => clearTimeout(timeoutId));
      stopTimeoutsRef.current.clear();

      // Clear all tension timeouts
      tensionTimeoutsRef.current.forEach(clearTimeout);
      tensionTimeoutsRef.current = [];

      // Clear tension state refs (prevents stale tension state)
      tensionSymbolsRef.current.clear();
      pendingTensionSymbolsRef.current.clear();
      tensionModeActiveRef.current = false;
      tensionActivationOrderRef.current = [];
      activeTensionSymbolRef.current = null;

      // CRITICAL: Reset spin counters so autoplay loop is never stuck after reload.
      // If a spin was in-progress when the reload flag was set, the stop callbacks on
      // the now-destroyed symbol controllers will never fire, leaving completedCountRef
      // < spinningCountRef and featureSpinComplete never dispatching.
      spinningCountRef.current = 0;
      completedCountRef.current = 0;
      isSpinningRef.current = false;
      manualStopTriggeredRef.current = false;
    }
  }, [isHoldAndWinMidFeatureReload, isHoldAndWinCompletionReload]);

  // Cleanup on unmount
  useEffect(() => {
    const stopTimeoutsMap = stopTimeoutsRef.current;

    return () => {
      if (autoStopTimeoutRef.current) {
        clearTimeout(autoStopTimeoutRef.current);
        autoStopTimeoutRef.current = null;
      }

      if (manualStopCleanupTimeoutRef.current) {
        clearTimeout(manualStopCleanupTimeoutRef.current);
        manualStopCleanupTimeoutRef.current = null;
      }

      if (tensionActivationTimeoutRef.current) {
        clearTimeout(tensionActivationTimeoutRef.current);
        tensionActivationTimeoutRef.current = null;
      }

      const tensionTimeouts = tensionTimeoutsRef.current;
      tensionTimeouts.forEach(clearTimeout);
      tensionTimeoutsRef.current = [];

      const stopTimeoutsSnapshot = Array.from(stopTimeoutsMap.values());
      stopTimeoutsSnapshot.forEach((timeoutId) => clearTimeout(timeoutId));
      stopTimeoutsMap.clear();
    };
  }, []);

  const contextValue = useMemo(() => {
    // If Hold & Win feature is disabled, provide no-op functions
    if (!FEATURES_CONFIG.HOLD_AND_WIN.ENABLED) {
      return {
        handleSpin: () => {},
        handleManualStop: () => {},
        handleSymbolStopComplete: () => {},
        registerProgressBarCallback: () => {},
      };
    }

    return {
      handleSpin,
      handleManualStop,
      handleSymbolStopComplete,
      registerProgressBarCallback,
    };
  }, [
    handleSpin,
    handleManualStop,
    handleSymbolStopComplete,
    registerProgressBarCallback,
  ]);

  return (
    <HoldAndWinControlContext.Provider value={contextValue}>
      {children}
    </HoldAndWinControlContext.Provider>
  );
};

export const useHoldAndWinControl = () => {
  const context = useContext(HoldAndWinControlContext);
  if (!context) {
    throw new Error(
      "useHoldAndWinControl must be used within a HoldAndWinControlContextProvider",
    );
  }
  return context;
};
